<template>
  <div
    v-if="isOpen"
    class="overlay bg-gray-600 bg-opacity-50 flex items-start justify-center z-50 pt-4 sm:pt-8"
    @click.self="closeDialog"
  >
    <div
      class="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      @click.stop
    >
      <div class="flex flex-row items-center justify-between mb-4">
        <h3 class="text-[#061B44] font-semibold text-lg">
          {{ $t("find-replace") }}
        </h3>
        <button
          @click="closeDialog"
          class="text-subDarkBlue hover:text-subDark opacity-70 hover:opacity-100 text-xl font-bold"
        >
          ×
        </button>
      </div>

      <!-- Target selector (Original/Translated) -->
      <div
        v-if="showTargetSelector"
        class="mb-4 flex flex-row space-x-2"
        :class="{ 'space-x-reverse': locale === 'ar' }"
      >
        <button
          @click="target = 'original'"
          :class="{
            'bg-subDark text-white': target === 'original',
            'bg-subWhite text-subDarkBlue': target !== 'original',
          }"
          class="px-4 py-2 rounded-md font-medium text-sm transition-colors"
        >
          {{ $t("source-text") }}
        </button>
        <button
          @click="target = 'translated'"
          :class="{
            'bg-subDark text-white': target === 'translated',
            'bg-subWhite text-subDarkBlue': target !== 'translated',
          }"
          class="px-4 py-2 rounded-md font-medium text-sm transition-colors"
        >
          {{ $t("translated-text") }}
        </button>
      </div>

      <!-- Find input -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-subDarkBlue mb-2">
          {{ $t("find") }}
        </label>
        <input
          ref="findInputRef"
          v-model="searchTerm"
          @input="onSearchInput"
          @keydown.enter.prevent="findNext"
          @keydown.escape="closeDialog"
          type="text"
          class="w-full px-3 py-2 border border-subDarkBlue rounded-lg focus:outline-none focus:ring-2 focus:ring-subBlue"
          :placeholder="$t('find')"
        />
      </div>

      <!-- Replace input -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-subDarkBlue mb-2">
          {{ $t("replace") }}
        </label>
        <input
          v-model="replaceTerm"
          @keydown.enter.prevent="replaceCurrent"
          @keydown.escape="closeDialog"
          type="text"
          class="w-full px-3 py-2 border border-subDarkBlue rounded-lg focus:outline-none focus:ring-2 focus:ring-subBlue"
          :placeholder="$t('replace')"
        />
      </div>

      <!-- Match counter -->
      <div
        v-if="searchTerm && totalMatches > 0"
        class="mb-4 text-sm text-subDarkBlue"
      >
        {{ $t("match-count", [currentMatchIndex + 1, totalMatches]) }}
      </div>
      <div
        v-else-if="searchTerm && totalMatches === 0"
        class="mb-4 text-sm text-red-600"
      >
        {{ $t("no-matches-found") }}
      </div>

      <!-- Action buttons -->
      <div
        class="flex flex-col sm:flex-row gap-2 justify-end"
        :class="{ 'sm:flex-row-reverse': locale === 'ar' }"
      >
        <button
          @click="findPrevious"
          :disabled="!searchTerm || totalMatches === 0"
          class="px-4 py-2 bg-white border border-subDarkBlue rounded-lg text-subDarkBlue font-medium text-sm hover:bg-subWhite disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {{ $t("find-previous") }}
        </button>
        <button
          @click="findNext"
          :disabled="!searchTerm || totalMatches === 0"
          class="px-4 py-2 bg-white border border-subDarkBlue rounded-lg text-subDarkBlue font-medium text-sm hover:bg-subWhite disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {{ $t("find-next") }}
        </button>
        <button
          @click="replaceCurrent"
          :disabled="!searchTerm || totalMatches === 0 || currentMatchIndex === -1"
          class="px-4 py-2 bg-white border border-subDarkBlue rounded-lg text-subDarkBlue font-medium text-sm hover:bg-subWhite disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {{ $t("replace") }}
        </button>
        <button
          @click="replaceAll"
          :disabled="!searchTerm || totalMatches === 0"
          class="px-4 py-2 bg-subDark text-white rounded-lg font-medium text-sm hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {{ $t("replace-all") }}
        </button>
        <button
          @click="closeDialog"
          class="px-4 py-2 bg-subWhite text-subDarkBlue rounded-lg font-medium text-sm hover:bg-opacity-80 whitespace-nowrap"
        >
          {{ $t("close") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { locale, t } = useI18n();

interface Props {
  isOpen: boolean;
  showTargetSelector?: boolean;
  initialTarget?: "original" | "translated";
  initialSearchTerm?: string;
}

const props = withDefaults(defineProps<Props>(), {
  showTargetSelector: true,
  initialTarget: "original",
  initialSearchTerm: "",
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "find", searchTerm: string, target: "original" | "translated"): void;
  (e: "find-next"): void;
  (e: "find-previous"): void;
  (e: "replace", replaceTerm: string): void;
  (e: "replace-all", replaceTerm: string): void;
  (e: "target-change", target: "original" | "translated"): void;
}>();

const findInputRef = ref<HTMLInputElement | null>(null);
const searchTerm = ref(props.initialSearchTerm);
const replaceTerm = ref("");
const target = ref<"original" | "translated">(props.initialTarget);
const currentMatchIndex = ref(-1);
const totalMatches = ref(0);

const closeDialog = () => {
  emit("close");
};

const onSearchInput = () => {
  if (searchTerm.value) {
    emit("find", searchTerm.value, target.value);
  } else {
    currentMatchIndex.value = -1;
    totalMatches.value = 0;
  }
};

const findNext = () => {
  if (searchTerm.value && totalMatches.value > 0) {
    emit("find-next");
  }
};

const findPrevious = () => {
  if (searchTerm.value && totalMatches.value > 0) {
    emit("find-previous");
  }
};

const replaceCurrent = () => {
  if (searchTerm.value && totalMatches.value > 0 && currentMatchIndex.value !== -1) {
    emit("replace", replaceTerm.value);
  }
};

const replaceAll = () => {
  if (searchTerm.value && totalMatches.value > 0) {
    emit("replace-all", replaceTerm.value);
  }
};

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      // Update target when dialog opens to reflect the initial target
      target.value = props.initialTarget;
      nextTick(() => {
        findInputRef.value?.focus();
      });
      if (props.initialSearchTerm) {
        searchTerm.value = props.initialSearchTerm;
        onSearchInput();
      }
    } else {
      searchTerm.value = "";
      replaceTerm.value = "";
      currentMatchIndex.value = -1;
      totalMatches.value = 0;
    }
  }
);

watch(
  () => props.initialTarget,
  (newTarget) => {
    // Update target when initialTarget prop changes (e.g., when opening from different button)
    if (props.isOpen) {
      target.value = newTarget;
    }
  }
);

watch(target, (newTarget) => {
  emit("target-change", newTarget);
  if (searchTerm.value) {
    emit("find", searchTerm.value, newTarget);
  }
});

// Expose methods for parent to update match info
defineExpose({
  setMatchInfo: (current: number, total: number) => {
    currentMatchIndex.value = current;
    totalMatches.value = total;
  },
  getSearchTerm: () => searchTerm.value,
  getReplaceTerm: () => replaceTerm.value,
  getTarget: () => target.value,
});
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 1rem;
}

@media (min-width: 640px) {
  .overlay {
    padding-top: 2rem;
  }
}
</style>

